import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, IconButton } from '@mui/material';
import { Button } from 'components/index';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { Division } from '../glossary/glossary.type';
import SelectedProjectChips from '../project/SelectedProjectChips';
import DivisionsList from './DivisionsList';

interface SelectedComparable {
  id: string;
  title: string;
  number: string;
}

interface DivisionSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  divisions: Division[];
  selectedComparables: SelectedComparable[];
  onApply: (selectedDivisionIds: string[]) => Promise<void>;
  isApplying: boolean;
}

const DivisionSelectionDialog: React.FC<DivisionSelectionDialogProps> = ({
  open,
  onClose,
  divisions,
  selectedComparables,
  onApply,
  isApplying,
}) => {
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedDivisionIds([]);
    }
  }, [open, divisions]);

  const handleApply = async () => {
    await onApply(selectedDivisionIds);
    setSelectedDivisionIds([]);
  };
  return (
    <Dialog
      open={open}
      onClose={!isApplying ? onClose : undefined}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '1500px',
          height: '900px',
        },
      }}
      fullWidth
      maxWidth='xl'
      scroll='body'
    >
      <DialogTitle sx={{ paddingBottom: 2 }}>
        Edit Divisions
        <IconButton onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }} size='large' disabled={isApplying}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2, pt: 1, m: 1 }}>
        {selectedComparables.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' color='text.primary' gutterBottom>
              Selected Projects ({selectedComparables.length}):
            </Typography>
            <SelectedProjectChips
              selectedIds={selectedComparables.map(s => s.id)}
              resolvedDetails={Object.fromEntries(selectedComparables.map(s => [s.id, { number: s.number, title: s.title }]))}
              showArrows
              allowDelete={false}
            />
          </Box>
        )}
        <DivisionsList
          divisions={divisions}
          selectedDivisionIds={selectedDivisionIds}
          onSelectionChange={(ids: string[]) => setSelectedDivisionIds(ids)}
          height={620}
          useCustomHeader={true}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1, borderTop: '1px solid #eee', m: 0 }}>
        <Button variant='outlined' onClick={onClose} disabled={isApplying}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleApply}
          disabled={isApplying}
          startIcon={isApplying ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DivisionSelectionDialog;
